import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SnackbarService } from 'app/core/snackbars/snackbar.service';
import {
    CreateShortcut,
    Shortcut,
    UpdateShortcut,
} from 'app/layout/common/shortcuts/shortcuts.types';
import {
    catchError,
    map,
    Observable,
    ReplaySubject,
    switchMap,
    take,
    tap,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ShortcutsService {
    private _shortcuts: ReplaySubject<Shortcut[]> = new ReplaySubject<
        Shortcut[]
    >(1);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _snackbarService: SnackbarService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for shortcuts
     */
    get shortcuts$(): Observable<Shortcut[]> {
        return this._shortcuts.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all shortcuts
     */
    getAll(): Observable<Shortcut[]> {
        return this._httpClient.get<Shortcut[]>('api/favorites').pipe(
            tap((shortcuts) => {
                this._shortcuts.next(shortcuts);
            }),
            catchError((err, _) => {
                this._snackbarService.error(err);
                throw err;
            })
        );
    }

    /**
     * Create a shortcut
     *
     * @param shortcut
     */
    create(shortcut: CreateShortcut): Observable<Shortcut> {
        return this.shortcuts$.pipe(
            take(1),
            switchMap((shortcuts) =>
                this._httpClient.post<Shortcut>('api/favorites', shortcut).pipe(
                    map((newShortcut) => {
                        this._shortcuts.next([...shortcuts, newShortcut]);

                        return newShortcut;
                    }),
                    catchError((err, _) => {
                        this._snackbarService.error(err);
                        throw err;
                    })
                )
            )
        );
    }

    /**
     * Update a shortcut's name and icon
     *
     * @param id
     * @param changes
     */
    update(id: string, changes: UpdateShortcut): Observable<boolean> {
        return this.shortcuts$.pipe(
            take(1),
            switchMap((shortcuts) =>
                this._httpClient
                    .patch<void>(`api/favorites/${id}`, changes)
                    .pipe(
                        map(() => {
                            const updatedShortcuts = shortcuts.map((item) =>
                                item.id === id ? { ...item, ...changes } : item
                            );

                            this._shortcuts.next(updatedShortcuts);

                            return true;
                        }),
                        catchError((err, _) => {
                            this._snackbarService.error(err);
                            throw err;
                        })
                    )
            )
        );
    }

    /**
     * Re-order the shortcuts
     *
     * @param ids The complete list of shortcut ids, in their new order. The
     *            server rejects a partial list.
     */
    reorder(ids: string[]): Observable<boolean> {
        return this.shortcuts$.pipe(
            take(1),
            switchMap((shortcuts) =>
                this._httpClient
                    .post<void>('api/favorites/reorder', { favoriteIds: ids })
                    .pipe(
                        map(() => {
                            const order = new Map(
                                ids.map((id, index) => [id, index])
                            );
                            const updatedShortcuts = [...shortcuts].sort(
                                (a, b) =>
                                    (order.get(a.id) ??
                                        Number.MAX_SAFE_INTEGER) -
                                    (order.get(b.id) ?? Number.MAX_SAFE_INTEGER)
                            );

                            this._shortcuts.next(updatedShortcuts);

                            return true;
                        }),
                        catchError((err, _) => {
                            this._snackbarService.error(err);
                            throw err;
                        })
                    )
            )
        );
    }

    /**
     * Delete a shortcut
     *
     * @param id
     */
    delete(id: string): Observable<boolean> {
        return this.shortcuts$.pipe(
            take(1),
            switchMap((shortcuts) =>
                this._httpClient.delete<void>(`api/favorites/${id}`).pipe(
                    map(() => {
                        const updatedShortcuts = shortcuts.filter(
                            (item) => item.id !== id
                        );

                        this._shortcuts.next(updatedShortcuts);

                        return true;
                    }),
                    catchError((err, _) => {
                        this._snackbarService.error(err);
                        throw err;
                    })
                )
            )
        );
    }
}
